import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { MouseEvent } from "react"
import { useNbrAuth } from "@/app/auth/useNbrAuth"
import { publicAssetUrl } from "@/shared/lib/publicAssetUrl"

export default function Footer() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const auth = useNbrAuth()
  const year = new Date().getFullYear()

  const handleHomeClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/") return
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const linkClass = "text-lib-text-secondary hover:text-lib-text text-sm font-medium transition-colors py-1 block"

  return (
    <footer className="bg-lib-card border-t border-lib-border">
      <div className="w-full px-6 md:px-10 lg:px-16 pt-16 md:pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Link to="/" onClick={handleHomeClick} aria-label={t("common.brandFull")}>
              <img
                src={publicAssetUrl("images/rgb-logo.svg")}
                alt={t("common.brandFull")}
                className="h-12 w-auto"
              />
            </Link>
            {!auth.isAuthenticated ? (
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-lib-accent hover:bg-lib-accent-dark text-white text-sm font-medium rounded-lg transition-colors w-fit whitespace-nowrap"
              >
                {t("nav.login")}
                <i className="ri-arrow-right-line" aria-hidden />
              </Link>
            ) : null}
            <p className="text-lib-text-secondary text-sm max-w-sm leading-relaxed">{t("common.footer.tagline")}</p>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1">
            <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-2">{t("common.footer.portalTitle")}</p>
            <Link to="/catalog" className={linkClass}>
              {t("nav.catalog")}
            </Link>
            <Link to="/news" className={linkClass}>
              {t("nav.news")}
            </Link>
            <Link to="/events" className={linkClass}>
              {t("nav.events")}
            </Link>
            <Link to="/deposits" className={linkClass}>
              {t("nav.deposits")}
            </Link>
            <Link to="/writers" className={linkClass}>
              {t("nav.writers")}
            </Link>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-2">{t("common.footer.infoTitle")}</p>
              <Link to="/about" className={linkClass}>
                {t("nav.about")}
              </Link>
              <Link to="/contacts" className={linkClass}>
                {t("nav.contacts")}
              </Link>
              <Link to="/help/agreement" className={linkClass} target="_blank" rel="noopener noreferrer">
                {t("common.footer.terms")}
              </Link>
              <Link to="/help/personal" className={linkClass} target="_blank" rel="noopener noreferrer">
                {t("common.footer.personalDataConsent")}
              </Link>
              <Link to="/help/privacy" className={linkClass} target="_blank" rel="noopener noreferrer">
                {t("common.footer.personalDataProcessingPolicy")}
              </Link>
            </div>
            <div>
              <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-3">{t("common.footer.socialTitle")}</p>
              <div className="flex items-center gap-3">
                <a
                  href="https://vk.ru/leninkaart"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-lib-border flex items-center justify-center text-lib-text-secondary hover:text-lib-accent hover:border-lib-accent transition-colors"
                  aria-label={t("common.footer.socialVk")}
                >
                  <i className="ri-vk-fill text-lg" aria-hidden />
                </a>
                <a
                  href="https://t.me/leninkaart"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-lib-border flex items-center justify-center text-lib-text-secondary hover:text-lib-accent hover:border-lib-accent transition-colors"
                  aria-label={t("common.footer.socialTelegram")}
                >
                  <i className="ri-telegram-fill text-lg" aria-hidden />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full px-6 md:px-10 lg:px-16 border-t border-lib-border py-6">
        <p className="text-lib-text-muted text-xs">{t("common.footer.copyright", { year })}</p>
      </div>
    </footer>
  )
}
